import React, {useState} from 'react';
import {StyleSheet} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';

import type {SettingsStackParamList} from '@/app/routes';
import {
  changePassword,
  fetchAccountProfile,
  fetchSessions,
  logoutEverywhere,
  revokeSessionById,
  setPassword,
} from '@/entities/account/api';
import {useAuthStore} from '@/entities/session/auth-store';
import {ApiKeysCard} from '@/features/manage-account-keys/ui/ApiKeysCard';
import {OAuthConnectionsCard} from '@/features/manage-account-keys/ui/OAuthConnectionsCard';
import {ApiError} from '@/shared/api';
import {formatTimestamp} from '@/shared/lib/format';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {EmptyState} from '@/shared/ui/empty-state';
import {FormRow} from '@/shared/ui/form';
import {AppInput} from '@/shared/ui/input';
import {ListRow, PagedRows} from '@/shared/ui/list-row';
import {Page} from '@/shared/ui/page';
import {SettingsNav} from '@/pages/settings/SettingsNav';
import {Section} from '@/shared/ui/section';
import {Skeleton} from '@/shared/ui/skeleton';
import {AppText} from '@/shared/ui/text';
import {radii, spacing} from '@/shared/ui/theme';

/**
 * Password and sessions.
 *
 * Two things here are easy to get wrong and are handled deliberately:
 *
 * 1. **Which password route.** An account created through a social login has no
 *    password to prove, so `POST /auth/change-password` cannot work for it — the
 *    server offers `POST /auth/set-password` instead. `profile.has_password` is what
 *    decides, and the form changes shape accordingly rather than showing a "current
 *    password" field nobody can fill in.
 *
 * 2. **Signing out everywhere includes HERE.** `POST /auth/logout-all` revokes every
 *    refresh token on the account, this app's included — so the local session has to
 *    be dropped straight afterwards. Leaving it up would show a working UI whose next
 *    call 401s, which reads as the app breaking rather than as the action succeeding.
 */
type Props = NativeStackScreenProps<SettingsStackParamList, 'Security'>;

const styles = StyleSheet.create({
  form: {gap: spacing(3)},
});

/** The server's own floor. Stated here so the message arrives before the round trip. */
const MIN_PASSWORD = 8;

export function SecurityScreen(_: Props) {
  const {t} = useTranslation();
  const qc = useQueryClient();
  const signOut = useAuthStore(s => s.signOut);

  const profile = useQuery({
    queryKey: ['account-profile'],
    queryFn: fetchAccountProfile,
  });

  const sessions = useQuery({
    queryKey: ['account-sessions'],
    queryFn: fetchSessions,
  });

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [done, setDone] = useState(false);

  const hasPassword = profile.data?.has_password ?? true;
  const mismatch = repeat.length > 0 && next !== repeat;
  const tooShort = next.length > 0 && next.length < MIN_PASSWORD;
  const canSubmit =
    next.length >= MIN_PASSWORD &&
    next === repeat &&
    (!hasPassword || current.length > 0);

  const submit = useMutation({
    mutationFn: () =>
      hasPassword ? changePassword(current, next) : setPassword(next),
    onSuccess: async () => {
      setCurrent('');
      setNext('');
      setRepeat('');
      setDone(true);
      await qc.invalidateQueries({queryKey: ['account-profile']});
    },
  });

  const revoke = useMutation({
    meta: {toast: 'toast.sessionRevoked'},
    mutationFn: (id: string) => revokeSessionById(id),
    onSuccess: () => qc.invalidateQueries({queryKey: ['account-sessions']}),
  });

  const [confirmAll, setConfirmAll] = useState(false);
  const everywhere = useMutation({
    mutationFn: logoutEverywhere,
    // The token this call was made with is now dead too — see the module note.
    onSettled: () => signOut(),
  });

  const errorOf = (e: unknown): string =>
    e instanceof ApiError ? e.message : t('errors.generic');

  return (
    <Page
      sideNav={<SettingsNav />}
      width="form"
      scroll
      testID="security-screen"
      title={t('more.security')}
      subtitle={t('more.securityHint')}
      >

      <Section
        title={t(hasPassword ? 'security.changePassword' : 'security.setPassword')}
        hint={t(hasPassword ? 'security.changeHint' : 'security.setHint')}>
        <Card padding={4} style={styles.form}>
          {hasPassword ? (
            <AppInput
              label={t('security.currentPassword')}
              value={current}
              onChangeText={value => {
                setCurrent(value);
                setDone(false);
              }}
              secure
              showPasswordLabel={t('a11y.showPassword')}
              hidePasswordLabel={t('a11y.hidePassword')}
              testID="security-current"
            />
          ) : null}
          <FormRow>
            <AppInput
              label={t('security.newPassword')}
              value={next}
              onChangeText={value => {
                setNext(value);
                setDone(false);
              }}
              secure
              hint={t('security.minLength', {count: MIN_PASSWORD})}
              error={tooShort ? t('security.tooShort', {count: MIN_PASSWORD}) : undefined}
              showPasswordLabel={t('a11y.showPassword')}
              hidePasswordLabel={t('a11y.hidePassword')}
              testID="security-new"
            />
            <AppInput
              label={t('security.repeatPassword')}
              value={repeat}
              onChangeText={setRepeat}
              secure
              error={mismatch ? t('security.mismatch') : undefined}
              showPasswordLabel={t('a11y.showPassword')}
              hidePasswordLabel={t('a11y.hidePassword')}
              testID="security-repeat"
            />
          </FormRow>
          {submit.isError ? (
            <Banner tone="error" title={errorOf(submit.error)} />
          ) : null}
          {done ? <Banner tone="success" title={t('security.passwordSaved')} /> : null}
          <AppButton
            title={t('actions.save')}
            disabled={!canSubmit}
            loading={submit.isPending}
            onPress={() => submit.mutate()}
            testID="security-save"
          />
        </Card>
      </Section>

      <Section title={t('security.sessions')} hint={t('security.sessionsHint')}>
        {sessions.isPending ? (
          <Skeleton height={120} radius={radii.lg} />
        ) : sessions.isError ? (
          <Banner tone="error" title={t('errors.generic')} />
        ) : (sessions.data?.sessions.length ?? 0) === 0 ? (
          <EmptyState title={t('security.noSessions')} />
        ) : (
          <PagedRows
            items={sessions.data.sessions}
            keyOf={session => session.id}
            testID="sessions-list"
            render={session => (
              <ListRow
                icon="devices"
                title={session.device_info || t('security.unknownDevice')}
                subtitle={t('security.sessionDates', {
                  created: formatTimestamp(session.created_at),
                  expires: formatTimestamp(session.expires_at),
                })}
                value={t('actions.revoke')}
                onPress={() => revoke.mutate(session.id)}
                testID={`session-${session.id}`}
              />
            )}
          />
        )}
        {revoke.isError ? (
          <Banner tone="error" title={errorOf(revoke.error)} />
        ) : null}
      </Section>

      {/* Both sit between sessions and the sign-out-everywhere button because all
          three answer one question — what, other than this app, can act as me — and
          the destructive one belongs last. */}
      <Section title={t('oauth.title')} hint={t('oauth.hint')}>
        <OAuthConnectionsCard hasPassword={hasPassword} />
      </Section>

      <Section title={t('apiKeys.title')} hint={t('apiKeys.hint')}>
        <ApiKeysCard />
      </Section>

      <Section title={t('security.signOutEverywhere')}>
        <Card padding={4} style={styles.form}>
          <AppText variant="caption">{t('security.signOutEverywhereHint')}</AppText>
          {confirmAll ? (
            <>
              <Banner tone="warning" title={t('security.signOutEverywhereConfirm')} />
              <AppButton
                title={t('security.signOutEverywhereDo')}
                tone="destructive"
                loading={everywhere.isPending}
                onPress={() => everywhere.mutate()}
                testID="security-logout-all-confirm"
              />
              <AppButton
                title={t('actions.cancel')}
                tone="ghost"
                onPress={() => setConfirmAll(false)}
              />
            </>
          ) : (
            <AppButton
              title={t('security.signOutEverywhere')}
              tone="neutral"
              onPress={() => setConfirmAll(true)}
              testID="security-logout-all"
            />
          )}
        </Card>
      </Section>
    </Page>
  );
}
